// File: src/App.jsx
// ADDED: HelmetProvider for SEO support

import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Home from './pages/Home';
import Cart from './components/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import Checkout from './pages/Checkout';
import Profile from './pages/Profile';
import Chat from './pages/Chat';
import ShopWithAI from './pages/ShopWithAI';
import AdminDashboard from './pages/admin/AdminDashboard';
import AddProduct from './pages/admin/AddProduct';
import AddCategory from './pages/admin/AddCategory';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetails from './pages/admin/AdminUserDetails';
import AdminCategories from './pages/admin/AdminCategories';
import AdminProducts from './pages/admin/AdminProducts';
import OrderManagement from './pages/admin/OrderManagement';
import SingleOrder from './pages/admin/SingleOrder';
import AdminPromos from './pages/admin/AdminPromos';
import AdminDelivery from './pages/admin/AdminDelivery';
import AdminBanners from './pages/admin/AdminBanners';
import AdminChats from './pages/admin/AdminChats';
import ChatThread from './pages/admin/ChatThread';
import AdminReviews from './pages/admin/AdminReviews';
import AdminMetaPixel from './pages/admin/AdminMetaPixel';
import ProductDetails from './pages/ProductDetails';
import OrderSuccess from './pages/OrderSuccess';
import OrderTracking from './pages/OrderTracking';
import CategoryList from './pages/CategoryList';
import CategoryProducts from './pages/CategoryProducts';
import SearchResult from './pages/SearchResult';
import ChatWidget from './components/ChatWidget';
import Footer from './components/Footer';
import { UserProvider, useUser } from './contexts/UserContext';
import { initPixel, trackPageView } from './utils/metaPixel';
import './App.css';

// Restrict admin pages to logged-in admins
const AdminRoute = ({ children }) => {
  const { user, isInitialized } = useUser();
  if (!isInitialized) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return children;
};

const ScrollToTopLayout = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const appContent = document.querySelector('.app-content');
    const previousHtmlBehavior = html.style.scrollBehavior;
    const previousBodyBehavior = body.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    const resetScroll = () => {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      body.scrollTop = 0;
      if (root) root.scrollTop = 0;
      if (appContent) appContent.scrollTop = 0;
    };

    resetScroll();
    const rafId = window.requestAnimationFrame(resetScroll);
    const timeoutId = window.setTimeout(resetScroll, 0);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      html.style.scrollBehavior = previousHtmlBehavior;
      body.style.scrollBehavior = previousBodyBehavior;
    };
  }, [location.key]);

  return <Outlet />;
};

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const { user, isInitialized: isUserInitialized } = useUser();

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const isAdminPage = location.pathname.startsWith('/admin');
  const isChatPage = location.pathname === '/chat';
  const isShopAIPage = location.pathname === '/shop-ai';

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!isUserInitialized || user?.isAdmin || isAdminPage) return;
    initPixel();
    trackPageView(location);
  }, [isUserInitialized, user?.isAdmin, isAdminPage, location]);

  return (
    <div className="app-container">
      <Header openCart={openCart} isCartOpen={isCartOpen} closeCart={closeCart} isAdminPage={isAdminPage} />
      <main className="app-main">
        <div className="app-content">
          <Routes>
            <Route element={<ScrollToTopLayout />}>
              <Route path="/" element={<Home openCart={openCart} />} />
              <Route path="/product/:slug" element={<ProductDetails openCart={openCart} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/shop-ai" element={<ShopWithAI openCart={openCart} />} />
              <Route path="/categories" element={<CategoryList openCart={openCart} />} />
              <Route path="/category/:slug" element={<CategoryProducts openCart={openCart} />} />
              <Route path="/search" element={<SearchResult openCart={openCart} />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/add-product" element={<AdminRoute><AddProduct /></AdminRoute>} />
              <Route path="/admin/add-category" element={<AdminRoute><AddCategory /></AdminRoute>} />
              <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
              <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetails /></AdminRoute>} />
              <Route path="/admin/orders" element={<AdminRoute><OrderManagement /></AdminRoute>} />
              <Route path="/admin/orders/:id" element={<AdminRoute><SingleOrder /></AdminRoute>} />
              <Route path="/admin/promos" element={<AdminRoute><AdminPromos /></AdminRoute>} />
              <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
              <Route path="/admin/meta-pixel" element={<AdminRoute><AdminMetaPixel /></AdminRoute>} />
              <Route path="/admin/delivery" element={<AdminRoute><AdminDelivery /></AdminRoute>} />
              <Route path="/admin/banners" element={<AdminRoute><AdminBanners /></AdminRoute>} />
              <Route path="/admin/chats" element={<AdminRoute><AdminChats /></AdminRoute>} />
              <Route path="/admin/chats/:convoId" element={<AdminRoute><ChatThread /></AdminRoute>} />
              <Route path="/order-success/:type/:id" element={<OrderSuccess />} />
              <Route path="/order-tracking" element={<OrderTracking />} />
            </Route>
          </Routes>
          {!isAdminPage && !isChatPage && !isShopAIPage && <ChatWidget />}
        </div>
        <Footer />
      </main>
      {!isAdminPage && !isChatPage && isCartOpen && <Cart closeCart={closeCart} />}
    </div>
  );
}

function AppWrapper() {
  return (
    <HelmetProvider>
      <Router>
        <UserProvider>
          <App />
        </UserProvider>
      </Router>
    </HelmetProvider>
  );
}

export default AppWrapper;
